/**
 * FloatingUserMenu - 리팩토링된 버전
 * 
 * 모듈 시스템 사용:
 * - FloatingMenu/menuConfig.js: 레벨별 메뉴 설정, 서브메뉴, 권한 체크
 * - FloatingMenu/MenuButton.jsx: 공통 버튼 컴포넌트
 * - hooks/useFloatingMenuTheme.js: 테마 훅
 * - hooks/useModalState.js: 모달 상태 감지 훅
 */
import React, { useContext, useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { UserContext } from '../contexts/UserContext';
import { useNavigate, useLocation } from 'react-router-dom';
import styles from '../styles/FloatingMenuStyles.module.css';
import { useLanguage } from '../utils/language';
import QuickInfoContainer from './QuickInfoContainer';
import { clearAllAuthData } from '../utils/auth';
import {
    isNotificationEligibleUserLevel,
    unsubscribeCurrentDevice
} from '../services/notificationService';

// 모듈 import
import {
    getMenuForLevel,
    SUBMENU_TEXT,
    STUDIO_SUBMENU,
    SETTINGS_SUBMENU,
    checkMenuItemAccess
} from './FloatingMenu/menuConfig';
import { MenuButton } from './FloatingMenu';

// 커스텀 훅
import useFloatingMenuTheme from '../hooks/useFloatingMenuTheme';
import useModalState from '../hooks/useModalState';

export default function FloatingUserMenu() {
    const { userInfo, setUserInfo } = useContext(UserContext);
    const navigate = useNavigate();
    const location = useLocation();
    const lang = useLanguage();

    // 커스텀 훅 사용
    const currentTheme = useFloatingMenuTheme();
    const isModalOpen = useModalState();

    // 서브메뉴 상태 (menuId로 어떤 서브메뉴가 열렸는지 관리)
    const [activeSubmenuId, setActiveSubmenuId] = useState(null);
    const [submenuPosition, setSubmenuPosition] = useState({ left: '50%', bottom: '110px' });
    const submenuRef = useRef(null);
    const buttonRefs = useRef({}); // 여러 버튼 ref를 관리

    // 서브메뉴 외부 클릭 감지
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (submenuRef.current && !submenuRef.current.contains(event.target)) {
                setActiveSubmenuId(null);
            }
        };

        if (activeSubmenuId) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [activeSubmenuId]);

    // 페이지 이동 시 서브메뉴 닫기
    useEffect(() => {
        setActiveSubmenuId(null);
    }, [location.pathname]);

    // 로그아웃 핸들러
    const handleLogout = async () => {
        try {
            if (isNotificationEligibleUserLevel(userInfo?.level)) {
                await unsubscribeCurrentDevice(userInfo.user_id).catch((error) => {
                    console.warn('현재 기기 알림 구독 정리 실패:', error.message);
                });
            }

            const response = await fetch('/api/logout', {
                method: 'POST',
                credentials: 'include',
            });
            if (response.ok) {
                clearAllAuthData();
                setUserInfo(null);
                window.location.href = '/';
            } else {
                clearAllAuthData();
                setUserInfo(null);
                window.location.href = '/';
            }
        } catch (error) {
            console.error('로그아웃 중 오류:', error);
            clearAllAuthData();
            setUserInfo(null);
            window.location.href = '/';
        }
    };

    const level = userInfo?.level || 'guest';
    const isActive = (path) => {
        if (!path) return false;
        const pathWithoutQuery = path.split('?')[0];
        // 홈 경로는 정확 매칭 (/client, /guest 등)
        if (pathWithoutQuery === '/client' || pathWithoutQuery === '/guest') {
            return location.pathname === pathWithoutQuery;
        }
        return location.pathname === pathWithoutQuery || location.pathname.startsWith(pathWithoutQuery);
    };

    // 메뉴 아이템 가져오기 (권한 체크 적용)
    const menuItems = getMenuForLevel(level).filter(item =>
        checkMenuItemAccess(item, userInfo, level)
    );

    // 서브메뉴 아이템 클릭 핸들러
    const handleSubmenuClick = (item) => {
        if (item.enabled && item.path) {
            navigate(item.path);
            setActiveSubmenuId(null);
        }
    };

    // 서브메뉴 토글 핸들러 (studioManagement, settings 등)
    const handleSubmenuToggle = (menuId) => {
        const buttonEl = buttonRefs.current[menuId];

        if (activeSubmenuId !== menuId && buttonEl) {
            const rect = buttonEl.getBoundingClientRect();
            const buttonCenterX = rect.left + rect.width / 2;
            const bottomFromViewport = window.innerHeight - rect.top + 10;

            const submenuWidth = 150;
            const halfWidth = submenuWidth / 2;

            let adjustedLeft = buttonCenterX;
            if (buttonCenterX - halfWidth < 10) {
                adjustedLeft = halfWidth + 10;
            } else if (buttonCenterX + halfWidth > window.innerWidth - 10) {
                adjustedLeft = window.innerWidth - halfWidth - 10;
            }

            setSubmenuPosition({
                left: `${adjustedLeft}px`,
                bottom: `${bottomFromViewport}px`
            });
        }

        setActiveSubmenuId(activeSubmenuId === menuId ? null : menuId);
    };

    // 현재 활성화된 서브메뉴 아이템들 가져오기 (권한 체크 적용)
    const getActiveSubmenuItems = () => {
        let items = [];
        if (activeSubmenuId === 'studioManagement') items = STUDIO_SUBMENU;
        if (activeSubmenuId === 'settings') items = SETTINGS_SUBMENU;

        // 권한 체크 필터링
        return items.filter(item => {
            if (!item.showOnlyFor) return true;
            return checkMenuItemAccess(item, userInfo, level);
        });
    };

    // 네비게이션 클릭 핸들러
    const handleNavigate = (path) => {
        if (path) navigate(path);
    };

    return (
        <>
            {/* 통합 블러 배경 레이어 */}
            <div
                className={styles.floatingMenuBlurBackground}
                style={{
                    display: isModalOpen ? 'none' : 'block'
                }}
            />

            <div
                className={styles.mainFloatingMenuWrapper}
                style={{
                    '--active-color': currentTheme.activeColor,
                    '--inactive-color': currentTheme.inactiveColor,
                    '--background-color': currentTheme.backgroundColor,
                    '--border-color': currentTheme.borderColor,
                    '--inactive-border-color': currentTheme.inactiveBorderColor || currentTheme.inactiveColor,
                    display: isModalOpen ? 'none' : 'flex'
                }}
            >
                {menuItems.map((item, index) => (
                    <MenuButton
                        key={item.id + '-' + index}
                        item={item}
                        isActive={isActive(item.isDynamic && item.getPath ? item.getPath(userInfo) : item.path)}
                        onClick={handleNavigate}
                        onLogout={handleLogout}
                        onSubmenuToggle={() => handleSubmenuToggle(item.id)}
                        buttonRef={item.hasSubmenu ? (el) => { buttonRefs.current[item.id] = el; } : undefined}
                        isSubmenuOpen={item.hasSubmenu && activeSubmenuId === item.id}
                        currentTheme={currentTheme}
                        lang={lang}
                        userInfo={userInfo}
                        styles={styles}
                    />
                ))}
            </div>

            {/* 서브메뉴 - 포탈로 body에 직접 렌더링 */}
            {activeSubmenuId && ReactDOM.createPortal(
                <div
                    ref={submenuRef}
                    className={styles.submenuPopup}
                    style={{
                        '--submenu-left': submenuPosition.left,
                        '--submenu-bottom': submenuPosition.bottom
                    }}
                >
                    {getActiveSubmenuItems().map(subItem => (
                        <button
                            key={subItem.id}
                            className={`${styles.submenuItem} ${subItem.enabled ? styles.enabled : styles.disabled}`}
                            onClick={() => handleSubmenuClick(subItem)}
                            style={{
                                color: subItem.enabled ? '#333' : '#aaa',
                                borderColor: subItem.enabled ? '#333' : '#ccc',
                            }}
                        >
                            {(SUBMENU_TEXT[subItem.textKey]?.[lang] || subItem.textKey).toUpperCase()}
                        </button>
                    ))}
                </div>,
                document.body
            )}

            {/* QuickInfoContainer */}
            <QuickInfoContainer
                userInfo={userInfo}
                currentTheme={currentTheme}
            />
        </>
    );
}
